import {
  FilterOption,
  FilterQuery,
  groupedOption,
  nonGroupedValues,
} from "@common/FilterComponent";

// Both the Care Journeys and Provider Referrals task tables build the same set of
// SortableTable filters, each repeating `dataType: "ajax"` and `query: {params, invalidate}`.
// This builder centralizes that boilerplate: pass the shared query plus whichever option
// lists a given table needs, and each filter is included only when its options are provided.
// Order matches the historical order in both tabs so saved filter views stay consistent.

type MultiselectOptions = nonGroupedValues[] | groupedOption[];

export interface TaskFilterOptionsInput {
  // The shared ajax query ({ params, invalidate }) stamped on every filter.
  query: FilterQuery;
  eventTypeOptions?: MultiselectOptions;
  eventSettingOptions?: MultiselectOptions;
  daysOpenOptions?: nonGroupedValues[];
  daysSinceDischargeOptions?: nonGroupedValues[];
  includeTaskDate?: boolean; // Flag indicating including Task Date range filter.
  taskTypeOptions?: MultiselectOptions;
  statusOptions?: MultiselectOptions;
  referralReviewStatusOptions?: MultiselectOptions;
  includeDueDate?: boolean; // Flag indicating including Due Date range filter.
  nextStepOptions?: MultiselectOptions;
  priorityOptions?: MultiselectOptions;
  includeLastViewed?: boolean; // Flag indicating including Last Viewed range filter.
  assigneeOptions?: MultiselectOptions;
  attestationStatusOptions?: MultiselectOptions;
  groupOptions?: MultiselectOptions;
}

export const buildTaskFilterOptions = (input: TaskFilterOptionsInput): FilterOption[] => {
  const {query} = input;
  const options: FilterOption[] = [];

  const multiselect = (key: string, label: string, opts?: MultiselectOptions) => {
    if (!opts) return;
    options.push({type: 'multiselect-search', key, label, options: opts, dataType: 'ajax', query});
  };

  const dateRange = (key: string, label: string, endLabel: string, include?: boolean) => {
    if (!include) return;
    options.push({type: 'date-range', key, label, endLabel, dataType: 'ajax', query});
  };

  multiselect('eventTypes', 'Event type', input.eventTypeOptions);
  multiselect('eventSettings', 'Event Setting', input.eventSettingOptions);
  multiselect('daysOpen', 'Days Open', input.daysOpenOptions);

  if (input.daysSinceDischargeOptions) {
    options.push({
      type: 'single-select',
      key: 'daysSinceDischarge',
      label: 'Days Since Discharge',
      options: input.daysSinceDischargeOptions,
      dataType: 'ajax',
      query,
    });
  }

  dateRange('taskDate', 'Task Start Date', 'Task End Date', input.includeTaskDate);
  multiselect('taskTypes', 'Task type', input.taskTypeOptions);
  multiselect('statuses', 'Status', input.statusOptions);
  multiselect('referralReviewStatuses', 'Referral Review Status', input.referralReviewStatusOptions);
  dateRange('dueDate', 'Due Start Date', 'Due End Date', input.includeDueDate);
  multiselect('nextSteps', 'Next Step', input.nextStepOptions);
  multiselect('priorities', 'Priority', input.priorityOptions);
  dateRange('lastViewed', 'Last Viewed Start Date', 'Last Viewed End Date', input.includeLastViewed);
  multiselect('assignees', 'Assignee', input.assigneeOptions);
  multiselect('attestationStatuses', 'Attestation Status', input.attestationStatusOptions);
  multiselect('groups', 'Group', input.groupOptions);

  return options;
};
